import { normalizeAppLocale, type AppLocale } from "./locale";
import type { ProductView, UnitPriceBasis } from "./types";

export const MAX_PRICE_WATCH_RULES = 30;
export const MAX_PRICE_WATCH_STORES = 40;
export const MAX_PRICE_WATCH_BASELINE_CATALOGS = 80;
/** One bounded response can represent every rule returned by the watch list. */
export const MAX_PRICE_WATCH_ALERTS = 60;

/** Only seeds NEW rules; saved rules always supply their own store scope. */
export function newPriceWatchStores(scope: "all" | "personal", stores: readonly string[]): string[] {
  return scope === "personal" ? [...stores] : [];
}

export type PriceWatchTargetKind = "product" | "query";
export type PriceWatchMatchMode = "exact" | "analogues" | "query";
export type PriceWatchPriceMode = "any" | "below";

export interface PriceWatchRuleDraft {
  id?: string;
  targetKind: PriceWatchTargetKind;
  matchMode: PriceWatchMatchMode;
  titleRu: string;
  titleRo: string;
  titleUk: string;
  sourceProductKey: string | null;
  productId: string | null;
  comparisonGroupId: string | null;
  query: string | null;
  categoryId: string | null;
  unitBasis: UnitPriceBasis | null;
  priceMode: PriceWatchPriceMode;
  maxPriceMdl: number | null;
  maxUnitPriceMdl: number | null;
  minimumDiscountPercent: number | null;
  stores: string[];
  baselineCatalogIds: string[];
  locale: AppLocale;
  enabled: boolean;
}

export interface PriceWatchRule extends PriceWatchRuleDraft {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export interface PriceAlertProduct {
  productKey: string;
  catalogId: string;
  productId: string | null;
  titleRu: string;
  titleRo: string;
  titleUk: string;
  store: string;
  catalogTitle: string;
  validUntil: string;
  image: string;
  newPriceMdl: number | null;
  oldPriceMdl: number | null;
  discountPercent: number | null;
  unitBasis: UnitPriceBasis | null;
  unitPriceMdl: number | null;
}

export interface PriceAlertEvent {
  id: string;
  ruleId: string;
  product: PriceAlertProduct;
  createdAt: string;
  sentAt: string | null;
  readAt: string | null;
}

export interface PriceWatchState {
  rules: PriceWatchRule[];
  alerts: PriceAlertEvent[];
  canNotify: boolean;
  /**
   * Enabled watch rules that currently have at least one qualifying offer.
   * Older servers may omit this during a rolling deployment, so consumers
   * must treat an absent value as zero rather than deriving it from history.
   */
  matchingRuleCount?: number;
}

export interface PriceWatchBadgeCounts {
  tracked: number;
  matching: number;
}

/** Media can be repackaged while the exact catalog offer remains unchanged. */
export function priceAlertImage(alert: PriceAlertEvent, product?: Pick<ProductView, "catalog_id" | "id" | "image">): string {
  return product && `${product.catalog_id}:${product.id}` === alert.product.productKey && product.image
    ? product.image
    : alert.product.image;
}

/** Display-only expiry: never delete or change the underlying watch rules. */
export function currentPriceAlerts(alerts: readonly PriceAlertEvent[], today: string): PriceAlertEvent[] {
  return alerts.filter(({ product: { validUntil } }) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validUntil) || validUntil < today) return false;
    const parsed = new Date(`${validUntil}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === validUntil;
  });
}

export interface PriceWatchResultGroup {
  rule: PriceWatchRule;
  alerts: PriceAlertEvent[];
}

/** A display projection only: reading/expiry never edits, disables or deletes a rule. */
export function groupPriceWatchResults(state: PriceWatchState, today: string): {
  withResults: PriceWatchResultGroup[];
  waiting: PriceWatchRule[];
  paused: PriceWatchRule[];
} {
  const byRule = new Map(state.rules.map(rule => [rule.id, new Map<string, PriceAlertEvent>()]));
  const newestFirst = [...currentPriceAlerts(state.alerts, today)].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
  for (const alert of newestFirst) {
    const products = byRule.get(alert.ruleId);
    // Keep the latest snapshot of a product per rule, never deduplicate across rules.
    if (products && !products.has(alert.product.productKey)) products.set(alert.product.productKey, alert);
  }
  const withResults: PriceWatchResultGroup[] = [];
  const waiting: PriceWatchRule[] = [];
  const paused: PriceWatchRule[] = [];
  for (const rule of state.rules) {
    const alerts = [...byRule.get(rule.id)!.values()];
    if (alerts.length) withResults.push({ rule, alerts });
    else (rule.enabled ? waiting : paused).push(rule);
  }
  // Read receipts never reshuffle the screen. Equal timestamps keep the server's rule order.
  withResults.sort((a, b) => b.alerts[0]!.createdAt.localeCompare(a.alerts[0]!.createdAt));
  return { withResults, waiting, paused };
}

function stringValue(value: unknown, maximumLength: number): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const prepared = value.trim();
  return prepared && prepared.length <= maximumLength ? prepared : null;
}

function nullableString(value: unknown, maximumLength: number): string | null {
  return value === null || value === undefined
    ? null
    : stringValue(value, maximumLength);
}

function finiteNumber(value: unknown, maximum: number): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= maximum
    ? Math.round(number * 100) / 100
    : null;
}

function uniqueStrings(
  value: unknown,
  maximumCount: number,
  maximumLength: number,
): string[] | null {
  if (!Array.isArray(value) || value.length > maximumCount) {
    return null;
  }
  const result = new Set<string>();
  for (const item of value) {
    const prepared = stringValue(item, maximumLength);
    if (!prepared) {
      return null;
    }
    result.add(prepared);
  }
  return [...result];
}

export function normalizePriceWatchDraft(
  value: unknown,
): PriceWatchRuleDraft | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const targetKind = candidate.targetKind;
  const matchMode = candidate.matchMode;
  const priceMode = candidate.priceMode;
  const locale = candidate.locale;
  const id = nullableString(candidate.id, 64) ?? undefined;
  const titleRu = stringValue(candidate.titleRu, 240);
  const titleRo = stringValue(candidate.titleRo, 240);
  const titleUk = stringValue(candidate.titleUk, 240) ?? titleRu;
  const stores = uniqueStrings(
    candidate.stores,
    MAX_PRICE_WATCH_STORES,
    120,
  );
  const baselineCatalogIds = uniqueStrings(
    candidate.baselineCatalogIds,
    MAX_PRICE_WATCH_BASELINE_CATALOGS,
    180,
  );
  if (
    !["product", "query"].includes(String(targetKind)) ||
    !["exact", "analogues", "query"].includes(String(matchMode)) ||
    !["any", "below"].includes(String(priceMode)) ||
    !normalizeAppLocale(locale) ||
    !titleRu ||
    !titleRo ||
    !titleUk ||
    !stores ||
    !baselineCatalogIds ||
    typeof candidate.enabled !== "boolean"
  ) {
    return null;
  }

  const sourceProductKey = nullableString(candidate.sourceProductKey, 360);
  const productId = nullableString(candidate.productId, 180);
  const comparisonGroupId = nullableString(candidate.comparisonGroupId, 180);
  const query = nullableString(candidate.query, 160);
  const categoryId = nullableString(candidate.categoryId, 180);
  const unitBasis = candidate.unitBasis;
  if (
    unitBasis !== null &&
    unitBasis !== "kg" &&
    unitBasis !== "l" &&
    unitBasis !== "10pcs" &&
    unitBasis !== "100pcs" &&
    unitBasis !== "100sheets"
  ) {
    return null;
  }
  if (targetKind === "query" && (!query || matchMode !== "query")) {
    return null;
  }
  if (
    targetKind === "product" &&
    (matchMode === "query" || !productId || !sourceProductKey)
  ) {
    return null;
  }
  if (matchMode === "analogues" && !comparisonGroupId) {
    return null;
  }

  const maxPriceMdl = finiteNumber(candidate.maxPriceMdl, 1_000_000);
  const maxUnitPriceMdl = finiteNumber(
    candidate.maxUnitPriceMdl,
    1_000_000,
  );
  const minimumDiscountPercent = finiteNumber(
    candidate.minimumDiscountPercent,
    100,
  );
  if (
    priceMode === "below" &&
    maxPriceMdl === null &&
    maxUnitPriceMdl === null
  ) {
    return null;
  }

  return {
    ...(id ? { id } : {}),
    targetKind: targetKind as PriceWatchTargetKind,
    matchMode: matchMode as PriceWatchMatchMode,
    titleRu,
    titleRo,
    titleUk,
    sourceProductKey,
    productId,
    comparisonGroupId,
    query,
    categoryId,
    unitBasis,
    priceMode: priceMode as PriceWatchPriceMode,
    maxPriceMdl,
    maxUnitPriceMdl,
    minimumDiscountPercent,
    stores,
    baselineCatalogIds,
    locale: locale as AppLocale,
    enabled: candidate.enabled,
  };
}

export function normalizePriceWatchRule(value: unknown): PriceWatchRule | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const draft = normalizePriceWatchDraft(candidate);
  const id = stringValue(candidate.id, 64);
  const createdAt = stringValue(candidate.createdAt, 64);
  const updatedAt = stringValue(candidate.updatedAt, 64);
  if (!draft || !id || !createdAt || !updatedAt) {
    return null;
  }
  return { ...draft, id, createdAt, updatedAt };
}

export function normalizePriceAlertEvent(
  value: unknown,
): PriceAlertEvent | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const productValue = candidate.product;
  if (!productValue || typeof productValue !== "object") {
    return null;
  }
  const product = productValue as Record<string, unknown>;
  const id = stringValue(candidate.id, 80);
  const ruleId = stringValue(candidate.ruleId, 64);
  const createdAt = stringValue(candidate.createdAt, 64);
  const productKey = stringValue(product.productKey, 360);
  const catalogId = stringValue(product.catalogId, 180);
  const titleRu = stringValue(product.titleRu, 240);
  const titleRo = stringValue(product.titleRo, 240);
  const titleUk = stringValue(product.titleUk, 240) ?? titleRu;
  const store = stringValue(product.store, 120);
  const catalogTitle = stringValue(product.catalogTitle, 180);
  const validUntil = stringValue(product.validUntil, 16);
  const image = stringValue(product.image, 500);
  const unitBasis = product.unitBasis;
  if (
    !id ||
    !ruleId ||
    !createdAt ||
    !productKey ||
    !catalogId ||
    !titleRu ||
    !titleRo ||
    !titleUk ||
    !store ||
    !catalogTitle ||
    !validUntil ||
    !image ||
    (unitBasis !== null &&
      unitBasis !== "kg" &&
      unitBasis !== "l" &&
      unitBasis !== "10pcs" &&
      unitBasis !== "100pcs" &&
      unitBasis !== "100sheets")
  ) {
    return null;
  }
  return {
    id,
    ruleId,
    product: {
      productKey,
      catalogId,
      productId: nullableString(product.productId, 180),
      titleRu,
      titleRo,
      titleUk,
      store,
      catalogTitle,
      validUntil,
      image,
      newPriceMdl: finiteNumber(product.newPriceMdl, 1_000_000),
      oldPriceMdl: finiteNumber(product.oldPriceMdl, 1_000_000),
      discountPercent: finiteNumber(product.discountPercent, 100),
      unitBasis,
      unitPriceMdl: finiteNumber(product.unitPriceMdl, 1_000_000),
    },
    createdAt,
    sentAt: nullableString(candidate.sentAt, 64),
    readAt: nullableString(candidate.readAt, 64),
  };
}

export function normalizePriceWatchState(value: unknown): PriceWatchState | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  if (
    !Array.isArray(candidate.rules) ||
    !Array.isArray(candidate.alerts) ||
    typeof candidate.canNotify !== "boolean"
  ) {
    return null;
  }
  const rules = candidate.rules.map(normalizePriceWatchRule);
  const alerts = candidate.alerts.map(normalizePriceAlertEvent);
  const matchingRuleCount = candidate.matchingRuleCount;
  if (rules.some((rule) => !rule) || alerts.some((alert) => !alert)) {
    return null;
  }
  if (
    matchingRuleCount !== undefined &&
    (typeof matchingRuleCount !== "number" ||
      !Number.isSafeInteger(matchingRuleCount) ||
      matchingRuleCount < 0)
  ) {
    return null;
  }
  return {
    rules: rules as PriceWatchRule[],
    alerts: alerts as PriceAlertEvent[],
    canNotify: candidate.canNotify,
    ...(matchingRuleCount === undefined
      ? {}
      : { matchingRuleCount: Number(matchingRuleCount) }),
  };
}

export function priceWatchBadgeCounts(
  state: PriceWatchState,
): PriceWatchBadgeCounts {
  const tracked = state.rules.filter((rule) => rule.enabled).length;
  const matching = Math.min(
    tracked,
    Math.max(0, Math.floor(state.matchingRuleCount ?? 0)),
  );
  return { tracked, matching };
}

function exactUnitPrice(product: ProductView): number | null {
  const unit = product.unit_price;
  return unit && !unit.is_range && unit.minimum_mdl > 0
    ? unit.minimum_mdl
    : null;
}

export function productPriceWatchDraft(
  product: ProductView,
  locale: AppLocale,
  stores: readonly string[],
  baselineCatalogIds: readonly string[],
): PriceWatchRuleDraft {
  const unitPrice = exactUnitPrice(product);
  const hasStrictAnalogues = Boolean(
    product.comparison_group_id && unitPrice !== null,
  );
  const itemPrice =
    product.new_price_mdl !== null && product.new_price_mdl > 0
      ? product.new_price_mdl
      : null;
  const belowCurrent = unitPrice !== null || itemPrice !== null;
  return {
    targetKind: "product",
    matchMode: hasStrictAnalogues ? "analogues" : "exact",
    titleRu: product.product_ru || product.product_ro,
    titleRo: product.product_ro || product.product_ru,
    titleUk: product.product_uk || product.product_ru || product.product_ro,
    sourceProductKey: `${product.catalog_id}:${product.id}`,
    productId: product.product_id ?? null,
    comparisonGroupId: product.comparison_group_id ?? null,
    query: null,
    categoryId: product.category_id ?? null,
    unitBasis: unitPrice !== null ? product.unit_price?.basis ?? null : null,
    priceMode: belowCurrent ? "below" : "any",
    maxPriceMdl: unitPrice === null ? itemPrice : null,
    maxUnitPriceMdl: unitPrice,
    minimumDiscountPercent: null,
    stores: [...stores],
    baselineCatalogIds: [...baselineCatalogIds],
    locale,
    enabled: true,
  };
}

export function refreshProductPriceWatchDraft(
  existing: PriceWatchRuleDraft,
  product: ProductView,
): PriceWatchRuleDraft {
  if (existing.targetKind !== "product") {
    return existing;
  }
  const current = productPriceWatchDraft(
    product,
    existing.locale,
    existing.stores,
    existing.baselineCatalogIds,
  );
  const gainsReferenceUnit =
    existing.unitBasis === null && current.unitBasis !== null;
  let maxPriceMdl = existing.maxPriceMdl;
  let maxUnitPriceMdl = existing.maxUnitPriceMdl;

  if (gainsReferenceUnit) {
    if (
      maxUnitPriceMdl === null &&
      maxPriceMdl !== null &&
      current.maxUnitPriceMdl !== null &&
      product.new_price_mdl !== null &&
      product.new_price_mdl > 0
    ) {
      maxUnitPriceMdl =
        Math.round(
          ((maxPriceMdl * current.maxUnitPriceMdl) / product.new_price_mdl) *
            100,
        ) / 100;
    } else if (maxUnitPriceMdl === null) {
      maxUnitPriceMdl = current.maxUnitPriceMdl;
    }
    maxPriceMdl = null;
  } else if (
    existing.unitBasis !== null &&
    current.unitBasis !== null &&
    existing.unitBasis !== current.unitBasis
  ) {
    maxPriceMdl = null;
    maxUnitPriceMdl = current.maxUnitPriceMdl;
  }

  const comparisonGroupId =
    current.comparisonGroupId ?? existing.comparisonGroupId;
  return {
    ...existing,
    titleRu: current.titleRu,
    titleRo: current.titleRo,
    titleUk: current.titleUk,
    sourceProductKey: current.sourceProductKey,
    productId: current.productId,
    comparisonGroupId,
    categoryId: current.categoryId ?? existing.categoryId,
    unitBasis: current.unitBasis ?? existing.unitBasis,
    matchMode:
      existing.matchMode === "analogues" && !comparisonGroupId
        ? "exact"
        : existing.matchMode,
    maxPriceMdl,
    maxUnitPriceMdl,
  };
}

export function queryPriceWatchDraft(
  query: string,
  locale: AppLocale,
  stores: readonly string[],
  baselineCatalogIds: readonly string[],
): PriceWatchRuleDraft {
  const title = query.trim().slice(0, 160);
  return {
    targetKind: "query",
    matchMode: "query",
    titleRu: title,
    titleRo: title,
    titleUk: title,
    sourceProductKey: null,
    productId: null,
    comparisonGroupId: null,
    query: title,
    categoryId: null,
    unitBasis: null,
    priceMode: "any",
    maxPriceMdl: null,
    maxUnitPriceMdl: null,
    minimumDiscountPercent: null,
    stores: [...stores],
    baselineCatalogIds: [...baselineCatalogIds],
    locale,
    enabled: true,
  };
}
